// usePreloadSettings.js
import { useEffect } from 'react';

// Context
import { useStartupStatus } from 'context/StartupStatusContext';
import { useUser } from 'context/UserContext';

// Services
import { getSettings, subscribeToSettings } from 'services/settings/SettingReadService';

/**
 * Custom hook to load settings for a specific user.
 */
const usePreloadSettings = () => {

  // User Context
  const { setIsUserSettingsLoading, user, setUserSettings } = useUser();
  const { clearStatus, setStatus } = useStartupStatus();

  useEffect(() => {
    if (!user?.key) {
      clearStatus('startup.settings');
      setIsUserSettingsLoading(false);
      setUserSettings(null);
      return;
    }

    let isDisposed = false;
    let hasResolvedStartupSettings = false;

    const resolveStartupSettings = (settings) => {
      if (isDisposed || hasResolvedStartupSettings) return;

      hasResolvedStartupSettings = true;
      clearStatus('startup.settings');
      setIsUserSettingsLoading(false);
      setUserSettings(settings);
    };

    setIsUserSettingsLoading(true);
    setStatus('startup.settings', {
      message: 'Loading your settings…',
      priority: 30,
    });

    const unsubscribe = subscribeToSettings(user.key, (settings) => {
      if (isDisposed) return;

      resolveStartupSettings(settings);
      setUserSettings(settings);
    }, {
      onError: (error) => {
        if (isDisposed) return;

        console.error('[usePreloadSettings] realtime settings listener error:', error);
        resolveStartupSettings(null);
      },
    });

    getSettings(user.key)
      .then((settings) => {
        resolveStartupSettings(settings);
      })
      .catch((error) => {
        if (isDisposed || hasResolvedStartupSettings) return;

        console.error('[usePreloadSettings] one-shot settings read failed:', error);
      });

    return () => {
      isDisposed = true;
      clearStatus('startup.settings');
      if (unsubscribe) unsubscribe();
    };
  }, [clearStatus, setIsUserSettingsLoading, setStatus, setUserSettings, user?.key]);

};

export default usePreloadSettings;
